import { api } from "@stoneflow/backend/convex/_generated/api"
import { Link, Outlet, createFileRoute } from "@tanstack/react-router"
import { useMutation, useQuery } from "convex/react"
import { Trash2 } from "lucide-react"
import { useState } from "react"
import { toast } from "sonner"
import { DeleteTenantDialog } from "#/components/admin/delete-tenant-dialog"
import { StatusPill } from "#/components/admin/status-pill"
import { RouteError } from "#/components/route-error"
import { Button } from "#/components/ui/button"
import { Skeleton } from "#/components/ui/skeleton"
import { useAppNavigate } from "#/hooks/use-app-navigate"
import { useIsPlatformOperator, useIsTenantAdmin } from "#/hooks/useIsTenantAdmin"
import { cn } from "#/lib/utils"

export const Route = createFileRoute("/_authenticated/admin/tenants/$orgId")({
	errorComponent: RouteError,
	component: TenantDetailLayout,
})

type SubTab = { label: string; to: string; operatorOnly?: boolean }

const SUB_TABS: readonly SubTab[] = [
	{ label: "Modules", to: "/admin/tenants/$orgId/modules", operatorOnly: true },
	{ label: "Users", to: "/admin/tenants/$orgId/users", operatorOnly: true },
	{ label: "Fields", to: "/admin/tenants/$orgId/fields", operatorOnly: true },
	{ label: "Billing", to: "/admin/tenants/$orgId/billing", operatorOnly: true },
	{ label: "Departments", to: "/admin/tenants/$orgId/departments" },
	{ label: "Reference Data", to: "/admin/tenants/$orgId/reference-data" },
] as const

const linkClass =
	"rounded-md px-2 py-1.5 text-xs text-muted-foreground hover:bg-accent/50 hover:text-foreground transition-colors whitespace-nowrap md:px-3 md:text-sm"
const activeLinkClass = cn(linkClass, "bg-accent text-foreground")

function TenantDetailLayout() {
	const { orgId } = Route.useParams()
	const { isOperator, isLoading: opsLoading } = useIsPlatformOperator()
	const { isAdmin: isTenantAdmin, isLoading: adminLoading } = useIsTenantAdmin()
	const navigate = useAppNavigate()
	const [deleteOpen, setDeleteOpen] = useState(false)

	const tenants = useQuery(api.platform.tenants.list, isOperator ? {} : "skip")
	const setStatus = useMutation(api.platform.tenants.setStatus)
	const tenantSummary = tenants?.find((t) => t.orgId === orgId)

	if (opsLoading || adminLoading) {
		return <Skeleton className="h-64 w-full" />
	}
	if (!isOperator && !isTenantAdmin) {
		return (
			<div className="py-12 text-center text-sm text-foreground-muted">
				This page is for tenant or platform admins.
			</div>
		)
	}

	const handleChangeStatus = async (status: "active" | "trial" | "disabled") => {
		try {
			await setStatus({ orgId, status })
			toast.success(`Tenant marked ${status}`)
		} catch (err) {
			toast.error(err instanceof Error ? err.message : "Failed to update status")
		}
	}

	const visibleTabs = SUB_TABS.filter((t) => !t.operatorOnly || isOperator)

	return (
		<div className="space-y-6">
			<header className="flex items-center justify-between border-b border-border pb-3">
				<div>
					<div className="text-xs text-foreground-muted">Tenants /</div>
					<h2 className="font-medium text-base">
						{tenantSummary?.name ?? <span className="text-foreground-muted">…</span>}
					</h2>
				</div>
				{isOperator && tenantSummary && (
					<div className="flex items-center gap-2">
						<StatusPill status={tenantSummary.status} editable onChange={handleChangeStatus} />
						<Button
							variant="destructive"
							size="sm"
							onClick={() => setDeleteOpen(true)}
							title="Delete tenant"
						>
							<Trash2 className="size-3" />
							Delete
						</Button>
					</div>
				)}
			</header>

			<nav className="flex flex-nowrap gap-1 overflow-x-auto border-b border-border pb-px">
				{visibleTabs.map((tab) => (
					<Link
						key={tab.to}
						to={tab.to}
						params={{ orgId }}
						className={linkClass}
						activeProps={{ className: activeLinkClass }}
					>
						{tab.label}
					</Link>
				))}
			</nav>

			<Outlet />

			{isOperator && tenantSummary && (
				<DeleteTenantDialog
					open={deleteOpen}
					onOpenChange={setDeleteOpen}
					orgId={orgId}
					tenantName={tenantSummary.name}
					onDeleted={() => navigate({ to: "/admin/tenants" })}
				/>
			)}
		</div>
	)
}
